import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';
import { NotificationService } from 'src/app/_share/_services/notification.service';
import { TransactionOverrideDialogComponent } from 'src/app/features/portal-management/components/transaction-override-dialog/transaction-override-dialog.component';
import { PortalManagementTransactionsService } from 'src/app/features/portal/_services/management/portal-management-transactions.service';

// The staff transaction ledger, used wherever transactions are listed for a
// subscription or an account: same columns, and the same Override / Void
// actions (row menu or right-click), gated by transactions.override /
// transactions.void. The parent owns loading; after an override or void the
// component emits `changed` so the parent reloads its list.
@Component({
  selector: 'share-transactions-table',
  templateUrl: './share-transactions-table.component.html',
  styleUrls: ['./share-transactions-table.component.scss'],
  providers: [DialogService, ConfirmationService]
})
export class ShareTransactionsTableComponent implements OnChanges {
  @Input() transactions: any[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No transactions found.';
  @Input() scrollHeight = '40vh';
  @Output() changed = new EventEmitter<void>();

  selectedRow: any = null;
  menuItems: MenuItem[] = [];
  canOverride = false;
  canVoid = false;
  rows: any[] = [];

  constructor(
    private authz: AuthorizationService,
    private dialogService: DialogService,
    private cs: ConfirmationService,
    private pmts: PortalManagementTransactionsService,
    private ns: NotificationService
  ) {
    this.canOverride = this.authz.hasPermission('transactions.override');
    this.canVoid = this.authz.hasPermission('transactions.void');
  }

  get hasActions(): boolean {
    return this.canOverride || this.canVoid;
  }

  ngOnChanges(): void {
    this.rows = this.transactions || [];
  }

  // Built per row so Void is disabled on a row that is already void.
  openMenu(row: any): void {
    this.selectedRow = row;
    const isVoid = ['voided', 'void'].includes((row?.status || '').toLowerCase());
    this.menuItems = [
      {
        label: 'Override',
        icon: 'pi pi-fw pi-pencil',
        visible: this.canOverride,
        command: () => this.override(row)
      },
      {
        label: isVoid ? 'Already void' : 'Void transaction',
        icon: 'pi pi-fw pi-times-circle',
        visible: this.canVoid,
        disabled: isVoid,
        command: () => this.confirmVoid(row)
      }
    ];
  }

  onRowContextMenu(event: any): void {
    this.openMenu(event?.data);
  }

  override(row: any): void {
    const ref = this.dialogService.open(TransactionOverrideDialogComponent, {
      data: { transaction: row },
      modal: true,
      showHeader: true,
      header: 'Override Transaction',
      width: '60%'
    });
    ref.onClose.subscribe((res) => {
      if (res) this.changed.emit();
    });
  }

  confirmVoid(row: any): void {
    this.cs.confirm({
      key: 'shareTransactionVoid',
      header: 'Void transaction',
      icon: 'pi pi-exclamation-triangle',
      message: `Void ${row.reference || 'this transaction'} (${row.transaction_description || row.transaction_type})? This cannot be undone.`,
      acceptLabel: 'Void',
      rejectLabel: 'Cancel',
      accept: () => {
        this.pmts.voidTransaction(row.id).subscribe({
          next: () => {
            this.ns.mainSuccess('Transaction voided.');
            this.changed.emit();
          },
          error: (err: any) => this.ns.mainError(err?.error?.detail || 'Could not void the transaction.')
        });
      }
    });
  }

  statusSeverity(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'voided':
      case 'void':
        return 'secondary';
      default:
        return 'danger';
    }
  }
}
